// src/components/ui/WfKebabMenu.tsx
//
// Kebab ⋮ + menu de ações no markup LITERAL do wireframe (varredura v2
// §5.6-C3): .skl-kebab > .kb (trigger) + .skl-menu (itens · .sep · .danger ·
// .soon com badge "em breve"). CSS: src/styles/wf-skills.css.
// Reusado por Skills (§08), Pipelines (§09), Modelos LLM (§14) e Negócios
// (§04) — cross-domain, por isso vive em ui/ (antes: components/skills/).

import { useEffect, useRef, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import { MoreVertical } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface WfKebabItem {
  key: string;
  label: string;
  icon: LucideIcon;
  onSelect?: () => void;
  danger?: boolean;
  /** Desabilitado com badge "em breve" (wireframe .skl-menu button.soon). */
  soon?: boolean;
  soonLabel?: string;
  disabled?: boolean;
  title?: string;
}

export type WfKebabEntry = WfKebabItem | 'sep';

interface WfKebabMenuProps {
  ariaLabel: string;
  items: WfKebabEntry[];
  /** Anti-bug Radix-like: a page pode suprimir o click fantasma pós-close. */
  onOpenChange?: (open: boolean) => void;
}

export function WfKebabMenu({ ariaLabel, items, onOpenChange }: WfKebabMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const setOpenNotify = (next: boolean) => {
    setOpen(next);
    onOpenChange?.(next);
  };

  // Fecha em click fora / Escape (comportamento do wireframe sklMenu()).
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        onOpenChange?.(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        onOpenChange?.(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onOpenChange]);

  return (
    <div
      ref={rootRef}
      className={cn('skl-kebab', open && 'open')}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className="kb"
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpenNotify(!open)}
      >
        <MoreVertical className="ic" aria-hidden />
      </button>
      {open && (
        <div className="skl-menu" role="menu" style={{ position: 'absolute', top: 34, right: 0 }}>
          {items.map((entry, i) =>
            entry === 'sep' ? (
              <div key={`sep-${i}`} className="sep" role="separator" />
            ) : (
              <button
                key={entry.key}
                type="button"
                role="menuitem"
                className={cn(entry.danger && 'danger', entry.soon && 'soon')}
                disabled={entry.soon || entry.disabled}
                title={entry.title}
                onClick={() => {
                  if (entry.soon || entry.disabled) return;
                  setOpenNotify(false);
                  entry.onSelect?.();
                }}
              >
                <entry.icon className="ic" aria-hidden />
                {entry.label}
                {entry.soon && <span className="b">{entry.soonLabel ?? 'em breve'}</span>}
              </button>
            ),
          )}
        </div>
      )}
    </div>
  );
}
